/**
 * DTO рабочих экранов менеджера — проживания (stays) + номера (rooms).
 * Спринт 11 (флот). Контракт сверен с internal/stay + internal/room
 * (interfaces/http/dto.go, handler.go):
 *
 *   GET   /v1/rooms?floor=&status=&limit=&offset=  → RoomListResponse
 *   GET   /v1/rooms/floors                         → FloorsResponse
 *   GET   /v1/rooms/calendar?from=&to=             → CalendarResponse
 *   PATCH /v1/rooms/{id}/status                    → RoomResponse
 *   GET   /v1/stays/{id}                           → StayResponse
 *   POST  /v1/stays/{id}/confirm|check-in|check-out|cancel
 *
 * Названия типов номеров берём из публичного каталога
 * GET /v1/public/{slug}/room-types (PublicRoomType) — поле type_id → name.
 */

/** Статус физического номера (room.domain.RoomStatus). */
export type RoomStatus =
  | 'available'
  | 'occupied'
  | 'pending_inspection'
  | 'cleaning'
  | 'repair'
  | 'maintenance'
  | 'out_of_order'
  | 'seasonal_closed';

/** Все статусы номера в порядке отображения. */
export const ROOM_STATUSES: readonly RoomStatus[] = [
  'available',
  'occupied',
  'pending_inspection',
  'cleaning',
  'repair',
  'out_of_order',
  'seasonal_closed',
];

/** Человекочитаемые подписи статусов номера (рус). */
export const ROOM_STATUS_LABELS: Readonly<Record<RoomStatus, string>> = {
  available: 'Свободен',
  occupied: 'Занят',
  pending_inspection: 'На проверке',
  cleaning: 'На уборке',
  repair: 'Ремонт',
  // maintenance — устаревший синоним repair, показываем как «Ремонт».
  maintenance: 'Ремонт',
  out_of_order: 'Закрыт/OOO',
  seasonal_closed: 'Закрыт/OOO',
};

/** Номер (toRoomResponse). */
export interface RoomView {
  id: string;
  tenant_id: string;
  type_id: string;
  number: string;
  floor: number;
  status: RoomStatus;
  notes?: string;
  /** Объектные ключи фото номера (или абсолютные URL), до 8, по порядку. */
  photos?: string[];
  created_at: string;
  updated_at: string;
}

/** GET /v1/rooms — { count, items }. */
export interface RoomListResponse {
  count: number;
  items: RoomView[];
}

/** Этаж-агрегат (FloorResponse). */
export interface FloorView {
  number: number;
  room_count: number;
  available_count: number;
}

/** GET /v1/rooms/floors. */
export interface FloorsResponse {
  floors: FloorView[];
}

/** PATCH /v1/rooms/{id}/status body. */
export interface UpdateRoomStatusInput {
  status: RoomStatus;
  notes?: string;
}

/** POST /v1/rooms body (CreateRoomRequest). */
export interface CreateRoomInput {
  tenant_id: string;
  type_id: string;
  number: string;
  floor: number;
}

/**
 * PATCH /v1/rooms/{id} body (UpdateRoomRequest). Все поля опциональны —
 * пропущенное не меняется. `photos` — полный упорядоченный список объектных
 * ключей (добавление/удаление/порядок одним запросом, до 8 на бэке).
 */
export interface UpdateRoomInput {
  number?: string;
  floor?: number;
  type_id?: string;
  photos?: string[];
}

/**
 * Тип номера (внутренний RoomTypeResponse — управление фондом).
 * display_name/description — мультиязычные карты ({ ru: '...' }).
 */
export interface RoomTypeView {
  id: string;
  tenant_id: string;
  code: string;
  display_name: Record<string, string>;
  description: Record<string, string>;
  base_price_kopecks: number;
  currency: string;
  capacity_adults: number;
  capacity_children: number;
  capacity_extra_beds: number;
  amenities: string[];
  status: string;
  created_at: string;
  updated_at: string;
}

/** GET /v1/rooms/types?tenant_id= — массив RoomTypeResponse. */
export type RoomTypesListResponse = RoomTypeView[];

/** POST /v1/rooms/types body (CreateRoomTypeRequest). */
export interface CreateRoomTypeInput {
  tenant_id: string;
  code: string;
  display_name?: Record<string, string>;
  description?: Record<string, string>;
  base_price_kopecks: number;
  currency?: string;
  capacity_adults: number;
  capacity_children: number;
  capacity_extra_beds: number;
  amenities?: string[];
}

/** PATCH /v1/rooms/types/{id} body (UpdateRoomTypeRequest — все поля опциональны). */
export interface UpdateRoomTypeInput {
  display_name?: Record<string, string>;
  description?: Record<string, string>;
  base_price_kopecks?: number;
  currency?: string;
  capacity_adults?: number;
  capacity_children?: number;
  capacity_extra_beds?: number;
  amenities?: string[];
}

/**
 * Строка списка номеров §4.2 (GET /v1/rooms/board → { count, items }).
 * Бэк джойнит rooms + cleanliness-overlay (cleanliness/last_cleaned_at) +
 * текущее проживание (гость/выезд) + признак «в очереди на уборку».
 */
export interface RoomBoardEntry {
  room_id: string;
  number: string;
  floor: number;
  type_id: string;
  /** Физический статус номера (available|occupied|cleaning|…). */
  status: RoomStatus;
  /** Чистота (overlay FSM): clean|dirty|inspected|out_of_order. */
  cleanliness: string;
  /** Последняя уборка (RFC3339) — undefined если данных нет/OOO. */
  last_cleaned_at?: string;
  out_of_order_reason?: string;
  /** Уборка номера в очереди (открытая автозадача) → суффикс «· в очереди». */
  in_cleaning_queue: boolean;
  current_stay_id?: string;
  current_guest_name?: string;
  /** Дата/время выезда текущего гостя (RFC3339). */
  current_check_out?: string;
}

/** GET /v1/rooms/board — { count, items }. */
export interface RoomBoardResponse {
  count: number;
  items: RoomBoardEntry[];
}

/** Источник смены статуса номера (room_status_events.source). */
export type RoomStatusEventSource = 'checkout' | 'cleaning_request' | 'manager' | 'system';

/** Одна запись истории смены статуса номера (с инициатором/источником). */
export interface RoomStatusEvent {
  from_status?: string;
  to_status: string;
  source: RoomStatusEventSource;
  /** Человекочитаемый источник (RU): «Менеджер» / «Выезд гостя» / «Заявка на уборку» / «Система». */
  source_label: string;
  actor_user_id?: string;
  /** Имя инициатора (для source=manager) — platform.users_identity.display_name. */
  actor_name?: string;
  /** Связанная сущность (stay_id / request_id). */
  ref_id?: string;
  reason?: string;
  created_at: string;
}

/** GET /v1/rooms/{id}/status-history — { room_id, count, events }. */
export interface RoomStatusHistoryResponse {
  room_id: string;
  count: number;
  events: RoomStatusEvent[];
}

/** Статус проживания (stay.domain.StayState). */
export type StayState =
  | 'pending'
  | 'confirmed'
  | 'checked_in'
  | 'checked_out'
  | 'cancelled'
  | 'no_show';

/** Сегмент занятости в календаре (CalendarOccupancyResponse). */
export interface CalendarOccupancy {
  stay_id: string;
  from: string;
  to: string;
  status: string;
  guest_id?: string;
  /** Имя гостя — бэк отдаёт в календаре (guest_name), карточка stay — нет. */
  guest_name?: string;
  /** Сумма проживания в копейках — бэк отдаёт в календаре (stays.total_amount). undefined, если не задана. */
  total_kopecks?: number;
  /** Валюта суммы (ISO, по умолчанию RUB). */
  currency?: string;
}

/** Строка календаря — один номер + его проживания (CalendarRoomResponse). */
export interface CalendarRoom {
  id: string;
  number: string;
  floor: number;
  type_id: string;
  status: RoomStatus;
  occupancies: CalendarOccupancy[];
}

/** GET /v1/rooms/calendar. */
export interface CalendarResponse {
  from: string;
  to: string;
  rooms: CalendarRoom[];
}

/** Управляющий фильтр списка «Проживания» (GET /v1/stays?status=). */
export type StayListStatus = 'in_house' | 'arrivals' | 'checked_out' | 'all';

/** Whitelisted колонки сортировки серверного списка (GET /v1/stays?sort=). */
export type StayListSort = 'room' | 'check_in' | 'check_out';

/** Параметры серверного списка проживаний. */
export interface StayListParams {
  status?: StayListStatus;
  sort?: StayListSort;
  order?: 'asc' | 'desc';
  q?: string;
  page?: number;
  page_size?: number;
}

/** Строка серверного списка проживаний (listManagedStays item). */
export interface StayListItem {
  stay_id: string;
  room_number: string;
  guest_name: string;
  check_in: string;
  check_out: string;
  status: string;
  total_kopecks: number;
  currency: string;
}

/** GET /v1/stays (management board, paginated). */
export interface StayListResponse {
  items: StayListItem[];
  total: number;
  page: number;
  page_size: number;
}

/** GET /v1/stays/{id} (projectStay). */
export interface StayResponse {
  id: string;
  tenant_id: string;
  room_id: string;
  guest_user_id: string;
  state: StayState;
  from: string;
  to: string;
  rate_kopecks: number;
  currency: string;
  total_kopecks: number;
  source: string;
  source_external_id?: string;
  created_at: string;
  updated_at: string;
  actual_check_in?: string;
  actual_check_out?: string;
  status_reason?: string;
}

/**
/**
 * Ответ POST /v1/stays/{id}/extensions (projectExtension) — запрос продления
 * проживания гостем (UC-G, design-guest §7). Менеджер апрувит/реджектит.
 */
export interface StayExtensionResponse {
  id: string;
  stay_id: string;
  state: string;
  original_to: string;
  new_to: string;
  fee_kopecks: number;
  currency: string;
  reject_reason?: string;
  approved_by?: string;
}

/**
 * Ответ POST /v1/stays/{id}/late-checkout (projectLateCheckout) — запрос
 * позднего выезда гостем (UC-G, design-guest §7).
 */
export interface StayLateCheckoutResponse {
  id: string;
  stay_id: string;
  guest_id: string;
  state: string;
  late_until: string;
  price_kopecks: number;
  complimentary: boolean;
  note?: string;
  reject_reason?: string;
  manager_id?: string;
}

/**
 * POST /v1/stays body (CreateBookingRequest). Форма менеджера «+ Бронь» на
 * шахматке. guest_user_id — UUID, генерируемый клиентом для нового гостя
 * (бэк лениво провижнит tenant-профиль гостя по этому id, fix #283).
 * guest_last_name/guest_first_name — имя гостя (фамилия видна в календаре).
 */
export interface CreateStayInput {
  room_id: string;
  guest_user_id: string;
  from: string;
  to: string;
  rate_kopecks: number;
  currency: string;
  source: string;
  guest_last_name?: string;
  guest_first_name?: string;
}


/* ===================== Иконки + удобства (правки Артёма 2026-06-17) ===================== */

/**
 * Иконка из глобального пака (GET /v1/icons → platform.icons). svg —
 * внутренняя разметка <svg> (paths/circles/rects); общие атрибуты
 * (viewBox/stroke=currentColor) проставляет компонент <DbIcon>. Ссылка на
 * иконку из удобства — по id.
 */
export interface IconView {
  id: string;
  key: string;
  label: string;
  svg: string;
  category?: string;
  sort_order: number;
}

/** GET /v1/icons → { icons: [...] }. */
export interface IconsResponse {
  icons: IconView[];
}

/**
 * Удобство из каталога тенанта (GET /v1/amenities). icon_id — ссылка на
 * IconView.id (может быть null). code — машинный код (совпадает с кодами в
 * room_types.amenities, расшифровывается этим каталогом).
 */
export interface AmenityView {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  icon_id: string | null;
  sort_order: number;
  is_active: boolean;
}

/** GET /v1/amenities → { amenities: [...] }. */
export interface AmenitiesResponse {
  amenities: AmenityView[];
}

/** POST /v1/amenities body. code необязателен — бэк выведет из name. */
export interface CreateAmenityInput {
  name: string;
  code?: string;
  icon_id?: string | null;
  sort_order?: number;
}

/** PUT /v1/amenities/{id} body (частичная правка). icon_id='' снимает иконку. */
export interface UpdateAmenityInput {
  name?: string;
  icon_id?: string | null;
  sort_order?: number;
  is_active?: boolean;
}
